import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    IconButton,
    Grid,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputAdornment,
} from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Done,
    Close,
    Search as SearchIcon,
} from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DynamicTable from "../../Dynamic-table";
import { useNavigate } from "react-router-dom";


interface OptionItem {
    _id: string;
    optionName: string;
    scheduleNotation: string[];
}

const CheckedOption: React.FC = () => {
    const [options, setOptions] = useState<OptionItem[]>([]);
    const [newOption, setNewOption] = useState<string>("");
    const [editOption, setEditOption] = useState<string>("");
    const [editingId, setEditingId] = useState<string | null>(null);
    const [scheduleNotation, setScheduleNotation] = useState<string[]>([]);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(100);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [searchInput, setSearchInput] = useState<string>("");
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const getOptions = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BASE_URL}/checked-status-options/getAllByPagination`,
                {
                    params: {
                        pageNo: page + 1,
                        count: rowsPerPage,
                        search: searchInput, // Added search
                    },
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );

            if (response.data.isSuccess) {
                const fetchedOptions = response.data.data;

                // Check if the response contains data
                if (fetchedOptions && fetchedOptions.length > 0) {
                    setOptions(fetchedOptions);
                    setTotalCount(response.data.totalCount);
                } else {
                    // No data in the backend, clear table silently
                    setOptions([]);
                    setTotalCount(0);
                }
            } else {
                // Only show toast for API-specific failure (not for empty data)
                console.error("API Error: Failed to fetch options");

            }
        } catch (error: unknown) {
            // Handle network or server errors
            if (error instanceof Error) {
                console.error("Error fetching options:", error.message);
                toast.error("An error occurred while fetching options.");
            } else {
                console.error("Unknown error occurred:", error);
                toast.error("An unexpected error occurred.");
            }
        }
    };





    useEffect(() => {
        getOptions(); // Fetch options on load and whenever search, page, or rowsPerPage changes
    }, [page, rowsPerPage, searchInput]);

    const resetForm = () => {
        setNewOption("");
        setEditOption("");
        setEditingId(null);
        setScheduleNotation([]);
    };

    const addOption = async () => {
        if (newOption.trim() && scheduleNotation.length > 0) {
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_BASE_URL}/checked-status-options/addCheckedStatusOption`,
                    {
                        optionName: newOption,
                        scheduleNotation,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    }
                );
                if (response.data.isSuccess) {
                    toast.success("Option added successfully");
                    getOptions();
                    resetForm();
                } else {
                    toast.error("Failed to add option");
                }
            } catch (error) {
                toast.error("Error adding option");
            }
        } else {
            toast.error("Please fill in all fields");
        }
    };

    const updateOption = async () => {
        if (editingId && editOption.trim()) {
            try {
                const response = await axios.patch(
                    `${process.env.REACT_APP_BASE_URL}/checked-status-options/updateById/${editingId}`,
                    {
                        optionName: editOption,
                        scheduleNotation,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    }
                );
                if (response.data.isSuccess) {
                    toast.success("Option updated successfully");
                    getOptions();
                    resetForm();
                } else {
                    toast.error("Failed to update option");
                }
            } catch (error) {
                toast.error("Error updating option");
            }
        } else {
            toast.error("Please fill in all fields");
        }
    };
    const handleOpenDialog = (id: string) => {
        setSelectedId(id);
        setOpenDialog(true);
    };


    const deleteOption = async () => {
        if (!selectedId) return;

        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_BASE_URL}/checked-status-options/deleteById/${selectedId}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );
            if (response.data.isSuccess) {
                toast.success("Option deleted successfully");
                getOptions();
            } else {
                toast.error("Failed to delete option");
            }
        } catch (error) {
            toast.error("Error deleting option");
        }

        setOpenDialog(false); // Close dialog after deletion
    };


    const handleEdit = (id: string, optionName: string, notations: string[]) => {
        setEditingId(id);
        setEditOption(optionName);
        setScheduleNotation(notations);
    };

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
    };

    return (
        <Box
            sx={{
                background: "#f8fafc",
                minHeight: "calc(100vh - 120px)",
                padding: { md: "32px", xs: "16px" },
                display: "flex",
                flexDirection: "column",
                gap: "24px",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexDirection: { md: "row", xs: "column" },
                    gap: "16px",
                }}
            >
                <Box>
                    <Typography
                        variant="h4"
                        sx={{
                            fontSize: "28px",
                            fontWeight: 700,
                            color: "#1e293b",
                            mb: 0.5,
                        }}
                    >
                        Manage Disposal Options
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configure and manage status options for disposal
                    </Typography>
                </Box>
                <TextField
                    placeholder="Search options..."
                    size="small"
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    sx={{
                        width: { md: "350px", xs: "100%" },
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            bgcolor: "white",
                            "& fieldset": { borderColor: "#e2e8f0" },
                            "&:hover fieldset": { borderColor: "#077DD3" },
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: "#64748b" }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Grid container spacing={4}>
                {/* Left Form Section */}
                <Grid item xs={12} md={4}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: "16px",
                            border: "1px solid #e2e8f0",
                            bgcolor: "white",
                            height: "fit-content",
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 700,
                                color: "#0f172a",
                                mb: 3,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            {editingId ? <EditIcon fontSize="small" color="primary" /> : <AddIcon fontSize="small" color="primary" />}
                            {editingId ? "Edit Option" : "Add New Option"}
                        </Typography>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                            <TextField
                                label="Option Name"
                                placeholder="e.g. Completed"
                                value={editingId ? editOption : newOption}
                                onChange={(e) =>
                                    editingId ? setEditOption(e.target.value) : setNewOption(e.target.value)
                                }
                                fullWidth
                                size="small"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "10px",
                                    },
                                }}
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel id="select-notations-label">Select Notations</InputLabel>
                                <Select
                                    labelId="select-notations-label"
                                    id="select-notations"
                                    multiple
                                    value={scheduleNotation || []}
                                    onChange={(e) => setScheduleNotation(e.target.value as string[])}
                                    sx={{ borderRadius: "10px" }}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {selected.map((val) => (
                                                <Typography key={val} variant="body2" sx={{ bgcolor: "#f1f5f9", px: 1, borderRadius: "4px" }}>
                                                    {val === "Abr." ? "Medical" : val}
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {["Abr.", "Math", "Urdu", "English"].map((option) => (
                                        <MenuItem key={option} value={option}>
                                            <Checkbox checked={(scheduleNotation || []).includes(option)} size="small" />
                                            <ListItemText primary={option === "Abr." ? "Medical" : option} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
                                {editingId && (
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={resetForm}
                                        sx={{
                                            borderRadius: "10px",
                                            textTransform: "none",
                                            color: "#64748b",
                                            borderColor: "#cbd5e1",
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={editingId ? updateOption : addOption}
                                    sx={{
                                        borderRadius: "10px",
                                        textTransform: "none",
                                        px: 3,
                                        boxShadow: "0 4px 6px -1px rgb(7 125 211 / 0.3)",
                                    }}
                                    startIcon={<Done />}
                                >
                                    {editingId ? "Update" : "Save Option"}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Right Table Section */}
                <Grid item xs={12} md={8}>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: "16px",
                            border: "1px solid #e2e8f0",
                            bgcolor: "white",
                        }}
                    >
                        <DynamicTable
                            headers={["SN", "Disposal Option", "Notations", "Actions"]}
                            data={options.map((item, index) => [
                                page * rowsPerPage + index + 1,
                                <Typography key={index} variant="body2" sx={{ fontWeight: 500, color: "#1e293b" }}>
                                    {item.optionName}
                                </Typography>,
                                <Box key={`not-${index}`} sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                    {item.scheduleNotation?.map((not, i) => (
                                        <Typography key={i} variant="caption" sx={{ bgcolor: "#f8fafc", border: "1px solid #e2e8f0", px: 1, py: 0.2, borderRadius: "4px" }}>
                                            {not.replaceAll("Abr.", "Medical")}
                                        </Typography>
                                    )) || "N/A"}
                                </Box>,
                            ])}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            handleChangePage={(e, newPage) => setPage(newPage)}
                            handleChangeRowsPerPage={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                            totalRows={totalCount}
                            actionButtons={[
                                {
                                    icon: <EditIcon sx={{ color: "#077DD3", fontSize: "20px" }} />,
                                    onClick: (rowIndex: any) =>
                                        handleEdit(options[rowIndex]._id, options[rowIndex].optionName, options[rowIndex].scheduleNotation),
                                },
                                {
                                    icon: <DeleteIcon sx={{ color: "#ef4444", fontSize: "20px" }} />,
                                    onClick: (rowIndex: any) => handleOpenDialog(options[rowIndex]._id),
                                }
                            ]}
                        />
                    </Paper>
                </Grid>
            </Grid>

            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                PaperProps={{
                    sx: { borderRadius: "16px", p: 1 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this disposal option? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={() => setOpenDialog(false)}
                        sx={{ borderRadius: "8px", textTransform: "none", color: "#64748b" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={deleteOption}
                        variant="contained"
                        color="error"
                        sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CheckedOption;
